import { Theme } from '@emotion/react';
import { createTheme, ThemeOptions } from '@mui/material';
import convert from 'color-convert';
import { colors } from './colors';

declare module '@mui/material/styles/createPalette' {
  interface Palette {
    royal10: {
      main: string;
    };
    tooltips: {
      main: string;
      background: string;
      text: string;
    };
    chatText: {
      main: string;
    };
  }
  interface PaletteOptions {
    royal10?: {
      main: string;
    };
    tooltips?: {
      main: string;
      background: string;
      text: string;
    };
    chatText?: {
      main: string;
    };
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    notesUserTitle: true;
    resourceTitle: true;
    notesFooter: true;
    wizardTitle: true;
    wizardSubTitle: true;
  }
}
const appFonts = {
  h1: 'Roboto-Black',
  h2: 'Roboto-Black',
  h3: 'Roboto-Black',
  h4: 'Roboto-Black',
  base: 'Roboto',
};

const fonts = {
  ...appFonts,
};

export const useThemeOptions = (): ThemeOptions => {
  const rgb = convert.hex.rgb('#000000'); // default to BG color

  return {
    typography: {
      fontFamily: fonts.base,
    },
    palette: {
      // mode: 'dark',
      primary: {
        main: colors.primary,
      },
      secondary: {
        main: colors.secondary,
      },
      success: {
        main: colors.success,
      },
      // warning: {
      // main: colors.warning,
      // },
      error: {
        main: colors.error,
      },
      background: {
        default: colors.background,
      },
    },
    components: {
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: 0,
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          InputLabelProps: { shrink: true },
        },
        styleOverrides: {
          root: {
            '&.white': {
              borderColors: 'white',
              '& .MuiInputBase-input': {
                color: 'white',
              },
              '& fieldset': {
                borderColor: 'white',
              },
              '& .MuiSvgIcon-root': {
                color: 'white',
              },
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            // borderRadius: ,
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            backgroundColor: `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.85)`,
          },
          invisible: {
            backgroundColor: 'transparent',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '100px',
            paddingLeft: '16px',
            paddingRight: '16px',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            p: 10,
            marginLeft: 8,
            marginRight: 8,
            minWidth: 0,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            paddingTop: 8,
            paddingBottom: 8,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: '0px -4px 25px rgba(0, 0, 0, 0.05)',
            borderRadius: '4px',
          },
        },
      },
      MuiSnackbar: {
        defaultProps: {
          anchorOrigin: { horizontal: 'center', vertical: 'top' },
          autoHideDuration: 5000,
        },
        styleOverrides: {
          root: {
            marginTop: '32px',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
          icon: {
            fontSize: 14,
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            '&.button-disclaimer-responsive': {
              display: 'block',
              textAlign: 'center',
              marginBottom: '16px',
            },
          },
        },
        variants: [
          {
            props: { variant: 'notesFooter' },
            style: {
              fontSize: 12,
              fontWeight: 500,
              fontFamily: fonts.base,
              lineHeight: '14.4px',
              fontStyle: 'normal',
              display: 'block',
            },
          },
          {
            props: { variant: 'notesUserTitle' },
            style: {
              fontSize: 14,
              fontWeight: 700,
              fontFamily: fonts.base,
              lineHeight: '16.8px',
              fontStyle: 'normal',
              display: 'block',
            },
          },
          {
            props: { variant: 'resourceTitle' },
            style: {
              fontSize: 20,
              fontWeight: 700,
              color: colors.darkNavy,
              fontFamily: fonts.base,
              lineHeight: 'normal',
              fontStyle: 'normal',
              display: 'block',
            },
          },
          {
            props: { variant: 'wizardTitle' },
            style: {
              fontSize: 28,
              fontWeight: 600,
              fontFamily: fonts.base,
              lineHeight: '34px',
              fontStyle: 'normal',
              display: 'block',
            },
          },
          {
            props: { variant: 'wizardSubTitle' },
            style: {
              fontSize: 24,
              fontWeight: 500,
              color: colors.black,
              fontFamily: fonts.base,
              lineHeight: '20px',
              fontStyle: 'normal',
              display: 'block',
            },
          },
        ],
      },
      MuiCssBaseline: {
        styleOverrides: (theme: any) => `
          @font-face {
            font-family: 'Roboto-Black';
            font-style: normal;
            font-display: swap;
            font-weight: 400;
            src: local('Causten Round'), local('Causten Round Regular'), url('./fonts/Roboto-Black.ttf') format('otf');
          }
          @font-face {
            font-family: 'Roboto-Bold';
            font-style: normal;
            font-display: swap;
            font-weight: 500;
            src: local('Roboto-Bold'), local('Causten Round Semi Bold'), url('./fonts/Roboto-Black-Bold.ttf') format('otf');
          }
        
        `,
      },
    },
  };
};

export const themeOptions: Theme = {
  typography: {
    fontFamily: fonts.base,
  },
  colorSchemes: {
    light: {
      palette: {
        primary: {
          main: colors.primary,
        },
        secondary: {
          main: colors.secondary,
        },
        background: {
          default: colors.background,
          paper: colors.paper,
        },
        text: {
          primary: colors.text,
          secondary: colors.textSecondary,
          tertiary: colors.textTertiary,
        },
        royal10: {
          main: colors.royal10,
        },

        tooltips: {
          main: colors.white,
          text: colors.darkText,
          background: colors.white,
        },
        chatText: {
          main: colors.primary,
        },
      },
    },
    dark: {
      palette: {
        primary: {
          main: colors.gray100,
        },
        secondary: {
          main: colors.secondary,
        },
        background: {
          default: colors.darkBackground,
          paper: colors.darkPaper,
        },
        text: {
          primary: colors.darkText,
          secondary: colors.darkTextSecondary,
          tertiary: colors.darkTextTertiary,
        },
        royal10: {
          main: colors.darkRoyal10,
        },
        tooltips: {
          main: colors.white,
        },
        chatText: {
          main: colors.white,
        },
      },
    },
  },
  palette: {
    primary: {
      main: colors.primary,
    },
    secondary: {
      main: colors.secondary,
    },
    success: {
      main: colors.success,
    },
    // warning: {
    // main: colors.warning,
    // },
    error: {
      main: colors.error,
    },
    background: {
      default: colors.background,
    },
    royal10: {
      main: colors.royal10,
    },
    tooltips: {
      main: colors.darkBackground2,
      background: '',
      text: '',
    },
    chatText: {
      main: colors.primary,
    },
  },
  components: {
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 0,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        InputLabelProps: { shrink: true },
      },
      styleOverrides: {
        root: {
          '&.white': {
            borderColors: 'white',
            '& .MuiInputBase-input': {
              color: 'white',
            },
            '& fieldset': {
              borderColor: 'white',
            },
            '& .MuiSvgIcon-root': {
              color: 'white',
            },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '100px',
          paddingLeft: '16px',
          paddingRight: '16px',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          p: 10,
          marginLeft: 8,
          marginRight: 8,
          minWidth: 0,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          paddingTop: 8,
          paddingBottom: 8,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0px -4px 25px rgba(0, 0, 0, 0.05)',
          borderRadius: '4px',
        },
      },
    },
    MuiSnackbar: {
      defaultProps: {
        anchorOrigin: { horizontal: 'center', vertical: 'top' },
        autoHideDuration: 5000,
      },
      styleOverrides: {
        root: {
          marginTop: '32px',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        icon: {
          fontSize: 14,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          '&.button-disclaimer-responsive': {
            display: 'block',
            textAlign: 'center',
            marginBottom: '16px',
          },
        },
      },
      variants: [
        {
          props: { variant: 'notesFooter' },
          style: {
            fontSize: 12,
            fontWeight: 500,
            fontFamily: fonts.base,
            lineHeight: '14.4px',
            fontStyle: 'normal',
            display: 'block',
          },
        },
        {
          props: { variant: 'notesUserTitle' },
          style: {
            fontSize: 14,
            fontWeight: 700,
            fontFamily: fonts.base,
            lineHeight: '16.8px',
            fontStyle: 'normal',
            display: 'block',
          },
        },
        {
          props: { variant: 'resourceTitle' },
          style: {
            fontSize: 20,
            fontWeight: 700,
            color: colors.darkNavy,
            fontFamily: fonts.base,
            lineHeight: 'normal',
            fontStyle: 'normal',
            display: 'block',
          },
        },
        {
          props: { variant: 'wizardTitle' },
          style: {
            fontSize: 28,
            fontWeight: 600,
            // color: colors.wizardTitle,
            fontFamily: fonts.base,
            lineHeight: '34px',
            fontStyle: 'normal',
            display: 'block',
          },
        },
        {
          props: { variant: 'wizardSubTitle' },
          style: {
            fontSize: 24,
            fontWeight: 500,
            color: colors.black,
            fontFamily: fonts.base,
            lineHeight: '20px',
            fontStyle: 'normal',
            display: 'block',
          },
        },
      ],
    },
    MuiCssBaseline: {
      styleOverrides: (theme: any) => `
        @font-face {
            font-family: 'Roboto-Black';
            font-style: normal;
            font-display: swap;
            font-weight: 400;
            src: local('Causten Round'), local('Causten Round Regular'), url('./fonts/Roboto-Black.ttf') format('otf');
          }
          @font-face {
            font-family: 'Roboto-Bold';
            font-style: normal;
            font-display: swap;
            font-weight: 500;
            src: local('Roboto-Bold'), local('Causten Round Semi Bold'), url('./fonts/Roboto-Black-Bold.ttf') format('otf');
          }
      `,
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }: { theme: any }) => ({
          backgroundColor:
            theme.palette.mode === 'dark'
              ? colors.listItemButton.dark.background
              : colors.listItemButton.light.background,
          color:
            theme.palette.mode === 'dark'
              ? colors.listItemButton.dark.text
              : colors.listItemButton.light.text,
          '&:hover': {
            backgroundColor:
              theme.palette.mode === 'dark'
                ? colors.listItemButton.dark.hover
                : colors.listItemButton.light.hover,
          },
          '&.Mui-selected': {
            backgroundColor:
              theme.palette.mode === 'dark'
                ? colors.listItemButton.dark.selected
                : colors.listItemButton.light.selected,
          },
        }),
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: ({ theme }: { theme: any }) => ({
          '& .MuiSwitch-track': {
            backgroundColor:
              theme.palette.mode === 'dark'
                ? colors.switch.dark.track
                : colors.switch.light.track,
          },
          '& .MuiSwitch-thumb': {
            backgroundColor:
              theme.palette.mode === 'dark'
                ? colors.switch.dark.thumb
                : colors.switch.light.thumb,
          },
          '& .Mui-checked .MuiSwitch-track': {
            backgroundColor:
              theme.palette.mode === 'dark'
                ? colors.switch.dark.checkedTrack
                : colors.switch.light.checkedTrack,
          },
        }),
      },
    },
  },
};

export default createTheme(themeOptions);
